import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { ArrowRight, Check, FlaskConical, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import {
  GET_DECISION_LAB,
  GET_DECISION_LAB_FOR_GROWNUP,
} from "@/graphql/queries/situations";
import { CREATE_SITUATION_SESSION } from "@/graphql/mutations/situations";
import { GET_STUDENTS } from "@/graphql/queries/students";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth/AuthContext";

/**
 * How many situations a session may hold. Six because there are six problem areas
 * — past that a session has to ask twice about the same area, which is the one
 * thing „iš skirtingų srovių" is for. Mirrors `SituationSession::MAX_SIZE`, which
 * is where it is enforced.
 */
const SESSION_SIZES = [1, 2, 3, 4, 5, 6];
const DEFAULT_SESSION_SIZE = 3;

type Situation = {
  id: string;
  key: string;
  title: string;
  questionCount: number;
  answeredCount: number;
  needsPracticeCount?: number | null;
  answeredGrades?: number[] | null;
  availableGrades?: number[] | null;
  submittedAt: string | null;
  reviewedAt?: string | null;
  personalized?: boolean;
};

type Theme = {
  key: string;
  name: string;
  intro: string | null;
  situations: Situation[];
};

type LabSession = {
  id: string;
  size: number;
  doneCount: number;
  done: boolean;
  started: boolean;
  nextKey: string | null;
  personalized: boolean;
  personalizationTheme?: string | null;
  createdAt: string;
  situations: { key: string; title: string; themeLabels: string[] }[];
};

/**
 * The Sprendimų laboratorija: situations about empathy, boundaries and handling
 * feelings.
 *
 * **Organised by problem area, and one row per problem.** None of these problems
 * belongs to a school year — a friend who will not take no for an answer is the
 * same problem at seven and at seventeen — so a list headed „1 klasė / 2 klasė"
 * told a twelve-year-old they were being handed a first-grader's problem.
 *
 * Each problem is written out per year as a variant, and the learner is served
 * the telling for their own age. The year never appears on this page for
 * anybody: it decides the words, not the problem.
 *
 * There are no levels, no percentages and nothing to unlock. `answeredCount`
 * says how much of a sheet is filled in, never how much of it is right.
 */
export default function DecisionLabPage() {
  const { student: me, isGuardian } = useAuth();
  const [params, setParams] = useSearchParams();

  const { data: studentsData } = useQuery(GET_STUDENTS, { skip: !isGuardian });
  const learners = ((studentsData as any)?.students ?? []) as {
    id: string;
    name: string;
    grade: number | null;
  }[];

  const selected = params.get("student") || (isGuardian ? learners[0]?.id : me?.id);
  const studentId = isGuardian ? selected : me?.id;

  const { data, loading } = useQuery(
    isGuardian ? GET_DECISION_LAB_FOR_GROWNUP : GET_DECISION_LAB,
    {
      variables: { studentId },
      skip: !studentId,
      fetchPolicy: "cache-and-network",
    }
  );

  const lab = (data as any)?.decisionLab;
  const themes: Theme[] = lab?.themes ?? [];
  const sessions: LabSession[] = lab?.sessions ?? [];
  const openSession = sessions.find((s) => !s.done);

  const pickLearner = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("student", id);
    setParams(next, { replace: true });
  };

  if (loading && !lab) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // A section behind a flag is absent, not broken. Nothing here explains what
  // they are missing or how to ask for it — an advert for a switch the reader
  // cannot reach is worse than silence.
  if (lab && !lab.enabled) {
    return (
      <div className="mx-auto max-w-2xl py-20 text-center text-muted-foreground">
        Ši skiltis šiuo metu neprieinama.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8 pb-16">
      <header className="space-y-2">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">Sprendimų laboratorija</h1>
        </div>
        <p className="text-muted-foreground">
          Situacijos, kuriose nėra vieno teisingo atsakymo. Perskaityk, pagalvok ir
          parašyk, kaip matai tu.
        </p>
      </header>

      {isGuardian && learners.length > 0 && (
        <Select value={studentId ?? undefined} onValueChange={pickLearner}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Pasirink mokinį" />
          </SelectTrigger>
          <SelectContent>
            {learners.map((l) => (
              <SelectItem key={l.id} value={l.id}>
                {l.name}
                {l.grade ? ` · ${l.grade} kl.` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* The guided path, above the shelves. Three unanswered situations from
          different areas is a better answer to „what now" than thirty-six on six
          shelves — and the shelves stay for a child who would rather go looking. */}
      {studentId && (
        <SessionsSection
          studentId={studentId}
          sessions={sessions}
          openSession={openSession}
          isGuardian={isGuardian}
        />
      )}

      {themes.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Situacijų dar nėra.
          </CardContent>
        </Card>
      )}

      {themes.length > 0 && (
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold">Visos situacijos</h2>
          <p className="text-sm text-muted-foreground">
            Arba pasirink pati (-s), ką skaityti.
          </p>
        </div>
      )}

      {themes.map((theme) => (
        <section key={theme.key} className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold">{theme.name}</h2>
            {theme.intro && (
              <p className="text-sm text-muted-foreground">{theme.intro}</p>
            )}
          </div>

          <div className="space-y-2">
            {theme.situations.map((situation) => (
              <SituationRow
                key={situation.key}
                situation={situation}
                studentId={studentId}
                isGuardian={isGuardian}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * The assigned sets.
 *
 * **Only a grown-up can assemble one**, which is not about trust: a session is what
 * gets reworded around the learner, and that is an AI call worth about half a
 * minute. A child pressing „start" and waiting for their own reading to be written
 * is the wrong shape, so a grown-up prepares it and the child finds it ready.
 *
 * The child sees one thing — the set waiting for them. Finished ones fold away
 * below: a session is kept rather than consumed (the answers are worth coming back
 * to), and a wall of ticked sets is not what „what now" should be answered with.
 */
function SessionsSection({
  studentId,
  sessions,
  openSession,
  isGuardian,
}: {
  studentId: string;
  sessions: LabSession[];
  openSession?: LabSession;
  isGuardian: boolean;
}) {
  const [count, setCount] = useState(String(DEFAULT_SESSION_SIZE));
  // Refetched rather than written into the cache by hand: a new session has to
  // appear in `decisionLab.sessions`, and Apollo has no way to know that a
  // returned object belongs in a list it did not come from. The alternative is a
  // hand-written cache update that silently stops matching the query.
  const [create, { loading: creating }] = useMutation(CREATE_SITUATION_SESSION, {
    refetchQueries: ["GetDecisionLab", "GetDecisionLabForGrownUp"],
    awaitRefetchQueries: true,
  });

  const finished = sessions.filter((s) => s.done);
  const link = (session: LabSession) =>
    `/lab/session/${session.id}${isGuardian ? `?student=${studentId}` : ""}`;

  const start = async () => {
    try {
      const result: any = await create({
        variables: { studentId, count: Number(count) },
      });
      const payload = result?.data?.createSituationSession;
      const errors = payload?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }
      // Nothing left to assign is not a failure to apologise for — it means the
      // learner has been through everything written for their age.
      if (!payload?.session) {
        toast.warning(payload?.shortfallReason ?? "Nėra ką duoti");
        return;
      }
      if (payload.shortfallReason) toast.warning(payload.shortfallReason);
      toast.success("Sesija paruošta");
    } catch (error) {
      toast.error("Nepavyko sukurti sesijos");
      console.error(error);
    }
  };

  return (
    <section className="space-y-3">
      {openSession ? (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="space-y-3 py-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">
                {openSession.started ? "Nebaigta sesija" : "Tau paruošta sesija"}
              </span>
              {openSession.personalized && (
                <Sparkles className="h-4 w-4 text-primary" />
              )}
              {/* Written in, out of asked. Never a score. */}
              <span className="text-sm text-muted-foreground">
                {openSession.doneCount} iš {openSession.size} atsakyta
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {openSession.situations.map((s) => (
                <Badge key={s.key} variant="outline" className="font-normal">
                  {s.themeLabels?.[0] ?? s.title}
                </Badge>
              ))}
            </div>
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to={link(openSession)}>
                {openSession.started ? "Tęsti" : "Pradėti"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        isGuardian && (
          <Card>
            <CardContent className="flex flex-wrap items-center gap-3 py-5">
              <div className="min-w-0 flex-1">
                <p className="font-semibold">Nauja sesija</p>
                <p className="text-sm text-muted-foreground">
                  Bus parinktos dar neatsakytos situacijos iš skirtingų srovių.
                </p>
              </div>
              <Select value={count} onValueChange={setCount}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SESSION_SIZES.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} situac.
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={start} disabled={creating}>
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sudaryti
              </Button>
            </CardContent>
          </Card>
        )
      )}

      {/* A second set cannot be assembled while one is unfinished: two sessions
          holding the same problem would have the child answer it once and see it
          ticked in both, which reads as one of them having been skipped. */}
      {openSession && isGuardian && (
        <p className="text-xs text-muted-foreground">
          Naują sesiją bus galima sudaryti, kai ši bus atlikta.
        </p>
      )}

      {finished.length > 0 && (
        <details className="rounded-lg border px-4 py-3">
          <summary className="cursor-pointer text-sm text-muted-foreground">
            Atliktos sesijos ({finished.length})
          </summary>
          <div className="mt-3 space-y-2">
            {finished.map((session) => (
              <Link
                key={session.id}
                to={link(session)}
                className="flex items-center gap-3 text-sm hover:underline"
              >
                <Check className="h-4 w-4 shrink-0 text-primary" strokeWidth={3} />
                <span className="flex-1">
                  {session.situations.map((s) => s.title).join(" · ")}
                </span>
                <span className="shrink-0 text-muted-foreground">
                  {session.size} situac.
                </span>
              </Link>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

function SituationRow({
  situation,
  studentId,
  isGuardian,
}: {
  situation: Situation;
  studentId?: string | null;
  isGuardian: boolean;
}) {
  const to = `/lab/${situation.key}${studentId ? `?student=${studentId}` : ""}`;
  const started = situation.answeredCount > 0;
  const done = started && situation.answeredCount >= situation.questionCount;

  return (
    <Link to={to} className="block">
      <Card className="transition-colors hover:border-primary/50">
        <CardContent className="flex items-center gap-4 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium">{situation.title}</span>
              {isGuardian && situation.personalized && (
                <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
              )}
            </div>
            {/* Guardian-only, and the server enforces it. „Already met, at these
                levels" is what a grown-up needs to choose what to give next
                without repeating themselves — and „you did the second-grade
                one" is the sentence this section exists to avoid saying to a
                child. */}
            {isGuardian && (situation.answeredGrades?.length ?? 0) > 0 && (
              <div className="mt-0.5 text-xs text-muted-foreground">
                spręsta: {situation.answeredGrades!.map((g) => `${g} kl.`).join(", ")}
                {(situation.availableGrades?.length ?? 0) > 0 &&
                  ` · parašyta: ${situation.availableGrades!.map((g) => `${g}`).join(", ")} kl.`}
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-3 text-sm text-muted-foreground">
            {/* Answered out of asked — how full the sheet is, never a score. */}
            {done ? (
              <span className="flex items-center gap-1 text-primary">
                <Check className="h-4 w-4" />
                atsakyta
              </span>
            ) : (
              <span>
                {started
                  ? `${situation.answeredCount} / ${situation.questionCount}`
                  : `${situation.questionCount} klausimai`}
              </span>
            )}
            {isGuardian && (situation.needsPracticeCount ?? 0) > 0 && (
              <span className="text-amber-600 dark:text-amber-500">
                {situation.needsPracticeCount} aptarti
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
